import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import { SidebarKeys } from 'support/types/localStorage';

const DEFAULT_WIDTH = 700;
const MIN_WIDTH = 150;
const MAX_WIDTH = 1200;
const COLLAPSED_THRESHOLD = 350;
const NAV_EXPANDED_WIDTH = 256;
const MIN_CONTENT_WIDTH = 600;

type ResizablePanelOptions = {
  initialWidth?: number;
  minWidth?: number;
};

export const useResizablePanel = (isOpen: boolean, options?: ResizablePanelOptions) => {
  const minWidth = options?.minWidth ?? MIN_WIDTH;
  const [panelWidth, setPanelWidth] = useState(options?.initialWidth ?? DEFAULT_WIDTH);
  const isResizing = useRef(false);
  const [navExpanded, setNavExpanded] = useLocalStorage(SidebarKeys.open, true);
  const navWasExpanded = useRef(navExpanded);
  const navExpandedRef = useRef(navExpanded);
  navExpandedRef.current = navExpanded;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const checkContentWidth = () => {
      const contentWidth = window.innerWidth - panelWidth - NAV_EXPANDED_WIDTH;
      if (contentWidth < MIN_CONTENT_WIDTH && navExpandedRef.current) {
        navWasExpanded.current = true;
        setNavExpanded(false);
      }
    };

    checkContentWidth();

    window.addEventListener('resize', checkContentWidth);
    return () => window.removeEventListener('resize', checkContentWidth);
  }, [panelWidth, isOpen, setNavExpanded]);

  const handleMouseDown = useCallback(() => {
    isResizing.current = true;
    let rafId = 0;
    window.document.body.style.setProperty('cursor', 'col-resize');
    window.document.body.style.setProperty('user-select', 'none');

    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) {
        return;
      }
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const newWidth = window.innerWidth - e.clientX;
        setPanelWidth(Math.min(MAX_WIDTH, Math.max(minWidth, newWidth)));
      });
    };

    const handleMouseUp = () => {
      isResizing.current = false;
      cancelAnimationFrame(rafId);
      window.document.body.style.removeProperty('cursor');
      window.document.body.style.removeProperty('user-select');
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [minWidth]);

  const restoreNav = useCallback(() => {
    if (navWasExpanded.current) {
      setNavExpanded(true);
    }
  }, [setNavExpanded]);

  const initialWidth = options?.initialWidth ?? DEFAULT_WIDTH;

  return {
    panelWidth,
    isCollapsed: panelWidth < COLLAPSED_THRESHOLD,
    handleMouseDown,
    expand: () => setPanelWidth(DEFAULT_WIDTH),
    collapse: () => setPanelWidth(initialWidth),
    restoreNav,
  };
};
